/**
 * VEYa — Chat Tab — Luxury AI Astrology Chat
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  FlatList,
  StyleSheet,
  Platform,
  KeyboardAvoidingView,
  Animated,
  Keyboard,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useChatStore, type DisplayMessage } from '../../src/stores/chatStore';
import { useUserStore } from '../../src/stores/userStore';
import { useOnboardingStore } from '../../src/stores/onboardingStore';
import type { UserProfile } from '../../src/types';

// ---------------------------------------------------------------------------
// Design Tokens
// ---------------------------------------------------------------------------
const COLORS = {
  bg: '#FDFBF7',
  primary: '#8B5CF6',
  primaryLight: '#A78BFA',
  gold: '#D4A547',
  goldLight: 'rgba(212, 165, 71, 0.08)',
  goldBorder: 'rgba(212, 165, 71, 0.15)',
  textPrimary: '#1A1A2E',
  textSecondary: '#6B6B80',
  textMuted: '#9B9BAD',
  white: '#FFFFFF',
  inputBg: '#F5F0E8',
  border: 'rgba(0, 0, 0, 0.04)',
  aiBubbleBg: '#FFFDF8',
  aiBubbleBorder: 'rgba(212, 165, 71, 0.12)',
};

// ---------------------------------------------------------------------------
// Suggested Questions
// ---------------------------------------------------------------------------
const SUGGESTED = [
  '✨ What does today hold for me?',
  '💜 Tell me about my love life',
  '🚀 Career guidance please',
  '🌙 What should I focus on this week?',
];

// ---------------------------------------------------------------------------
// Typing Indicator Component
// ---------------------------------------------------------------------------
function TypingIndicator() {
  const dot1 = useRef(new Animated.Value(0)).current;
  const dot2 = useRef(new Animated.Value(0)).current;
  const dot3 = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const createBounce = (dot: Animated.Value, delay: number) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(dot, { toValue: 1, duration: 300, useNativeDriver: true }),
          Animated.timing(dot, { toValue: 0, duration: 300, useNativeDriver: true }),
        ]),
      );

    const anim1 = createBounce(dot1, 0);
    const anim2 = createBounce(dot2, 150);
    const anim3 = createBounce(dot3, 300);

    anim1.start();
    anim2.start();
    anim3.start();

    return () => {
      anim1.stop();
      anim2.stop();
      anim3.stop();
    };
  }, [dot1, dot2, dot3]);

  const dotStyle = (anim: Animated.Value) => ({
    transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [0, -6] }) }],
  });

  return (
    <View style={styles.typingContainer}>
      <View style={styles.typingBubble}>
        <Text style={styles.typingLabel}>VEYa is consulting the stars</Text>
        <View style={styles.dotsRow}>
          {[dot1, dot2, dot3].map((dot, i) => (
            <Animated.View key={i} style={[styles.dot, dotStyle(dot)]} />
          ))}
        </View>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Message Bubble Component
// ---------------------------------------------------------------------------
function MessageBubble({ item }: { item: DisplayMessage }) {
  const isUser = item.role === 'user';
  const time = new Date(item.timestamp);
  const timeStr = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <View style={[styles.messageRow, isUser && styles.messageRowUser]}>
      {!isUser && (
        <View style={styles.avatarContainer}>
          <Text style={styles.avatarText}>✨</Text>
        </View>
      )}
      <View style={styles.bubbleColumn}>
        <View style={[styles.bubble, isUser ? styles.userBubble : styles.aiBubble]}>
          <Text style={[styles.bubbleText, isUser && styles.userBubbleText]}>
            {item.content}
          </Text>
        </View>
        <Text style={[styles.timeText, isUser && styles.timeTextUser]}>{timeStr}</Text>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Main Chat Screen
// ---------------------------------------------------------------------------
export default function ChatScreen() {
  const insets = useSafeAreaInsets();
  const [input, setInput] = useState('');
  const flatListRef = useRef<FlatList>(null);

  // Stores
  const { messages, isLoading, sendMessage, clearChat } = useChatStore();
  const userFromStore = useUserStore((s) => s.user);
  const onboardingData = useOnboardingStore((s) => s.data);

  // Build a UserProfile from available data
  const userProfile: UserProfile = userFromStore || {
    id: 'local',
    user_id: 'local',
    name: onboardingData.name || null,
    email: null,
    avatar_url: null,
    birth_date: onboardingData.birthDate ? String(onboardingData.birthDate) : null,
    birth_time: onboardingData.birthTime ? String(onboardingData.birthTime) : null,
    birth_time_known: onboardingData.birthTimeKnown,
    birth_time_precision: onboardingData.birthTimePrecision,
    birth_time_range: null,
    birth_place: onboardingData.birthPlace || null,
    birth_lat: onboardingData.birthLat,
    birth_lng: onboardingData.birthLng,
    timezone: onboardingData.timezone,
    sun_sign: onboardingData.sunSign || null,
    moon_sign: null,
    rising_sign: null,
    chinese_zodiac: null,
    vedic_nakshatra: null,
    life_path_number: null,
    onboarding_completed: true,
    focus_areas: onboardingData.focusAreas || [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [messages.length, isLoading]);

  const handleSend = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;
      setInput('');
      Keyboard.dismiss();
      sendMessage(trimmed, userProfile);
    },
    [isLoading, sendMessage, userProfile],
  );

  const sunSign = userProfile.sun_sign;
  const greeting = sunSign
    ? `Your ${sunSign} stars await`
    : 'Your stars await';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <KeyboardAvoidingView
      style={[styles.root, { paddingTop: insets.top }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
    >
      <StatusBar style="dark" />

      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={styles.headerTitle}>Talk to VEYa</Text>
          <Text style={styles.headerSubtitle}>Your AI Astrologer</Text>
        </View>
        {messages.length > 0 && (
          <Pressable
            onPress={clearChat}
            style={styles.clearBtn}
            hitSlop={12}
          >
            <Text style={styles.clearBtnText}>New Chat</Text>
          </Pressable>
        )}
      </View>

      {/* Empty State */}
      {messages.length === 0 && !isLoading ? (
        <View style={styles.empty}>
          <View style={styles.emptyIconCircle}>
            <Text style={styles.emptyEmoji}>✨</Text>
          </View>
          <Text style={styles.emptyTitle}>Ask me anything</Text>
          <Text style={styles.emptySubtitle}>
            About your stars, your path, your purpose
          </Text>
          {sunSign && (
            <View style={styles.signBadge}>
              <Text style={styles.signBadgeText}>☉ {sunSign}</Text>
            </View>
          )}
          <View style={styles.suggestions}>
            {SUGGESTED.map((s, i) => (
              <Pressable
                key={i}
                onPress={() => handleSend(s.replace(/^[^\w]*/, ''))}
                style={({ pressed }) => [
                  styles.suggestion,
                  pressed && styles.suggestionPressed,
                ]}
              >
                <Text style={styles.suggestionText}>{s}</Text>
              </Pressable>
            ))}
          </View>
        </View>
      ) : (
        /* Message List */
        <FlatList
          ref={flatListRef}
          data={messages}
          keyExtractor={(m) => m.id}
          contentContainerStyle={styles.messageList}
          showsVerticalScrollIndicator={false}
          renderItem={({ item }) => <MessageBubble item={item} />}
          ListFooterComponent={isLoading ? <TypingIndicator /> : null}
          onContentSizeChange={() =>
            flatListRef.current?.scrollToEnd({ animated: true })
          }
        />
      )}

      {/* Input Bar */}
      <View style={[styles.inputBar, { paddingBottom: Math.max(insets.bottom, 8) + 8 }]}>
        <TextInput
          style={styles.input}
          value={input}
          onChangeText={setInput}
          placeholder="Ask the stars..."
          placeholderTextColor={COLORS.textMuted}
          onSubmitEditing={() => handleSend(input)}
          returnKeyType="send"
          multiline
          maxLength={500}
          editable={!isLoading}
        />
        <Pressable
          onPress={() => handleSend(input)}
          disabled={!input.trim() || isLoading}
          style={({ pressed }) => [
            styles.sendBtn,
            (!input.trim() || isLoading) && styles.sendBtnDisabled,
            pressed && styles.sendBtnPressed,
          ]}
        >
          <Text style={styles.sendBtnText}>↑</Text>
        </Pressable>
      </View>
    </KeyboardAvoidingView>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: COLORS.bg,
  },

  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
  },
  headerLeft: {},
  headerTitle: {
    fontSize: 22,
    fontFamily: 'PlayfairDisplay-Bold',
    color: COLORS.textPrimary,
  },
  headerSubtitle: {
    fontSize: 13,
    fontFamily: 'Inter-Regular',
    color: COLORS.textMuted,
    marginTop: 2,
  },
  clearBtn: {
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 20,
    backgroundColor: COLORS.goldLight,
    borderWidth: 1,
    borderColor: COLORS.goldBorder,
  },
  clearBtnText: {
    fontSize: 12,
    fontFamily: 'Inter-Medium',
    color: COLORS.gold,
  },

  // Empty state
  empty: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  emptyIconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: COLORS.goldLight,
    borderWidth: 1,
    borderColor: COLORS.goldBorder,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  emptyEmoji: {
    fontSize: 36,
  },
  emptyTitle: {
    fontSize: 24,
    fontFamily: 'PlayfairDisplay-Bold',
    color: COLORS.textPrimary,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 15,
    fontFamily: 'Inter-Regular',
    color: COLORS.textMuted,
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 22,
  },
  signBadge: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: 'rgba(139, 92, 246, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(139, 92, 246, 0.15)',
    marginBottom: 28,
  },
  signBadgeText: {
    fontSize: 13,
    fontFamily: 'Inter-Medium',
    color: COLORS.primary,
  },
  suggestions: {
    width: '100%',
    gap: 10,
  },
  suggestion: {
    backgroundColor: COLORS.white,
    borderRadius: 14,
    padding: 16,
    borderWidth: 1,
    borderColor: COLORS.goldBorder,
  },
  suggestionPressed: {
    backgroundColor: COLORS.goldLight,
  },
  suggestionText: {
    fontSize: 15,
    fontFamily: 'Inter-Medium',
    color: COLORS.textSecondary,
    textAlign: 'center',
  },

  // Message list
  messageList: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    paddingBottom: 8,
  },

  // Message row
  messageRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 16,
    maxWidth: '85%',
  },
  messageRowUser: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse',
  },

  // Avatar
  avatarContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: COLORS.goldLight,
    borderWidth: 1,
    borderColor: COLORS.goldBorder,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    marginBottom: 16, // align with bubble bottom
  },
  avatarText: {
    fontSize: 14,
  },

  // Bubble
  bubbleColumn: {},
  bubble: {
    borderRadius: 18,
    padding: 14,
    paddingHorizontal: 16,
  },
  userBubble: {
    backgroundColor: COLORS.primary,
    borderBottomRightRadius: 4,
  },
  aiBubble: {
    backgroundColor: COLORS.aiBubbleBg,
    borderBottomLeftRadius: 4,
    borderWidth: 1,
    borderColor: COLORS.aiBubbleBorder,
  },
  bubbleText: {
    fontSize: 15,
    fontFamily: 'Inter-Regular',
    color: COLORS.textPrimary,
    lineHeight: 23,
  },
  userBubbleText: {
    color: COLORS.white,
  },
  timeText: {
    fontSize: 11,
    fontFamily: 'Inter-Regular',
    color: COLORS.textMuted,
    marginTop: 4,
    marginLeft: 4,
  },
  timeTextUser: {
    textAlign: 'right',
    marginRight: 4,
    marginLeft: 0,
  },

  // Typing indicator
  typingContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 16,
  },
  typingBubble: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.aiBubbleBg,
    borderRadius: 18,
    borderBottomLeftRadius: 4,
    borderWidth: 1,
    borderColor: COLORS.aiBubbleBorder,
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 10,
  },
  typingLabel: {
    fontSize: 13,
    fontFamily: 'Inter-Regular',
    color: COLORS.gold,
    fontStyle: 'italic',
  },
  dotsRow: {
    flexDirection: 'row',
    gap: 4,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: COLORS.gold,
  },

  // Input bar
  inputBar: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    backgroundColor: COLORS.white,
    alignItems: 'flex-end',
    gap: 10,
  },
  input: {
    flex: 1,
    minHeight: 44,
    maxHeight: 100,
    backgroundColor: COLORS.inputBg,
    borderRadius: 22,
    paddingHorizontal: 18,
    paddingTop: Platform.OS === 'ios' ? 12 : 10,
    paddingBottom: Platform.OS === 'ios' ? 12 : 10,
    fontSize: 15,
    fontFamily: 'Inter-Regular',
    color: COLORS.textPrimary,
    lineHeight: 20,
  },
  sendBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: COLORS.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: Platform.OS === 'ios' ? 0 : 0,
  },
  sendBtnDisabled: {
    backgroundColor: COLORS.primaryLight,
    opacity: 0.5,
  },
  sendBtnPressed: {
    opacity: 0.8,
  },
  sendBtnText: {
    color: COLORS.white,
    fontSize: 18,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '700',
  },
});
